import { useEffect, useState } from "react";
import { Search, X, Check, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export const PageTitle = ({ title, sub, children, icon: Icon, color = "text-brand" }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div className="flex items-center gap-3">
      {Icon && <span className="w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center shadow-md shadow-brand/20"><Icon className="w-6 h-6" /></span>}
      <div><h1 className={`text-2xl sm:text-3xl font-black ${color}`}>{title}</h1>{sub && <p className="text-slate-500">{sub}</p>}</div>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const SearchBox = ({ value, onChange, placeholder, testid = "search-input" }) => (
  <div className="relative flex-1 min-w-[220px]">
    <Search className="w-5 h-5 absolute top-1/2 -translate-y-1/2 right-3 text-slate-400" />
    <input data-testid={testid} className="field field-sm pr-10" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

export const Table = ({ head, children, testid }) => (
  <div className="card overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600"><tr>{head.map((h) => <th key={h} className="text-right font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);
export const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

export const Stat = ({ label, value, icon: Icon, tone = "slate", testid, to }) => {
  const tones = { blue: ["bg-blue-600", "bg-blue-50/70"], red: ["bg-red-500", "bg-red-50/70"], green: ["bg-emerald-600", "bg-green-50/70"], orange: ["bg-orange-500", "bg-orange-50/70"], slate: ["bg-brand", "bg-white"] };
  const [ib, bg] = tones[tone] || tones.slate;
  const body = (<>
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-white shadow-md ${ib}`}><Icon className="w-7 h-7" /></div>
    <div className="min-w-0 flex-1"><div className="text-slate-600 text-sm font-bold">{label}</div><div className="text-2xl font-black truncate text-slate-900">{value}</div></div>
    {to && <ChevronLeft className="w-5 h-5 text-slate-400 shrink-0" />}
  </>);
  const cls = `card p-5 flex items-center gap-4 fade-up ${bg}`;
  return to
    ? <Link to={to} data-testid={testid} className={`${cls} hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98] transition-[transform,box-shadow]`}>{body}</Link>
    : <div className={cls} data-testid={testid}>{body}</div>;
};

export function useDebounced(v, ms = 350) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export function DriverPicker({ selected, onChange, multi = false, testid = "driver-picker" }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (dq.trim().length < 1) { setRes([]); return; }
    api.get("/drivers", { params: { q: dq, status: "active" } }).then((r) => setRes(r.data.items)).catch(() => {});
  }, [dq]);
  const has = (d) => selected.some((s) => s.id === d.id);
  const toggle = (d) => onChange(has(d) ? selected.filter((s) => s.id !== d.id) : multi ? [...selected, d] : [d]);
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {selected.map((d) => (
          <span key={d.id} className="inline-flex items-center gap-1 bg-brand text-white rounded-full ps-3 pe-1 py-1 text-sm">
            {d.full_name} <span className="ltr text-amber-300">{d.serial}</span>
            <button type="button" onClick={() => toggle(d)} className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </span>
        ))}
      </div>
      <SearchBox value={q} onChange={setQ} placeholder="ابحث بالاسم أو الرقم التسلسلي أو الهاتف أو لوحة الشاحنة" testid={`${testid}-search`} />
      {res.length > 0 && (
        <div className="border rounded-xl max-h-56 overflow-y-auto divide-y bg-white">
          {res.map((d) => (
            <button type="button" key={d.id} data-testid={`${testid}-option-${d.serial}`} onClick={() => toggle(d)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-right">
              <span>{d.full_name} <span className="ltr text-slate-500 text-sm">{d.serial}</span> {d.vehicle_plate && <span className="text-xs text-blue-700 ltr inline-block">{[d.vehicle_admin_number, d.vehicle_plate].filter(Boolean).join("/")}</span>}</span>
              {has(d) && <Check className="w-5 h-5 text-green-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const FileInput = ({ onChange, testid, accept = "image/*,application/pdf" }) => (
  <input data-testid={testid} type="file" accept={accept} onChange={(e) => onChange(e.target.files?.[0] || null)}
    className="block w-full text-sm file:me-3 file:rounded-lg file:border-0 file:bg-brand file:text-white file:px-4 file:py-2 file:font-bold" />
);
